import { DateTime } from "luxon";
import type { DepartmentLocation, NurseFullRole } from "@/utils/models";
import { sameClockTime } from "@/utils/shiftPeriod";
import type { LocationConfigSlimData } from "./locationConfigData";

/**
 * Recurring shifts reference their location by name only — there is no foreign key to
 * `shift_template`. A config and a shift are considered the same slot when this tuple matches, on
 * both sides of the wire: `locationMatchesShiftName` / `locationMatchesShiftTimes` /
 * `locationMatchesShiftRoleOrJob` in `../utils.ts` on the client, and
 * `get_department_recurring_shifts_by_location` on the server.
 */
export type ShiftMatchKey = Pick<LocationConfigSlimData, "name" | "shiftStartTime" | "shiftEndTime" | "roles" | "jobId">;

export const pickShiftMatchKey = (data: LocationConfigSlimData): ShiftMatchKey => ({
	name: data.name.trim(),
	shiftStartTime: data.shiftStartTime,
	shiftEndTime: data.shiftEndTime,
	roles: data.roles,
	jobId: data.jobId,
});

/**
 * Mirrors `locationMatchesShiftRoleOrJob` (../utils.ts): the slot stays matched as long as every
 * suggested role is still covered, and "all" covers everything. The matcher tests containment
 * rather than equality, so *broadening* the role set orphans nothing.
 */
const rolesStillCover = (suggested: (NurseFullRole | "all")[], current: (NurseFullRole | "all")[]): boolean =>
	current.includes("all") || suggested.every((role) => current.includes(role));

/**
 * One field of the match key that the user moved away from the suggestion. Hours are pre-formatted
 * for display (12-hour); the rest carry raw values — job ids stay ids, roles stay enum members —
 * and are resolved to labels by `UpdateShiftsWarning`, which owns translation.
 */
export type ShiftMatchChange =
	| { field: "name"; from: string; to: string }
	| { field: "hours"; from: { start: string; end: string }; to: { start: string; end: string } }
	| { field: "roles"; from: (NurseFullRole | "all")[]; to: (NurseFullRole | "all")[] }
	| { field: "job"; from: string; to: string };

/**
 * Which parts of the match key the user changed, and therefore which existing recurring shifts
 * would be orphaned by a plain create.
 *
 * Roles and job are never both load-bearing: `locationMatchesShiftRoleOrJob` takes the job branch
 * only when job management is on and both sides carry a job id, and the server mirrors that —
 * `get_department_recurring_shifts_by_location` filters on `job_id` when
 * `use_jobs=bool(old_location_config.job_id)` and on `nurse.role` otherwise. Diffing the inert one
 * would warn about a change that cannot move a single shift.
 */
export const diffShiftMatchKey = (suggested: ShiftMatchKey, current: ShiftMatchKey, jobManagementEnabled: boolean): ShiftMatchChange[] => {
	const changes: ShiftMatchChange[] = [];

	if (suggested.name !== current.name) {
		changes.push({ field: "name", from: suggested.name, to: current.name });
	}
	if (suggested.shiftStartTime !== current.shiftStartTime || suggested.shiftEndTime !== current.shiftEndTime) {
		changes.push({
			field: "hours",
			from: {
				start: DateTime.fromFormat(suggested.shiftStartTime, "HH:mm").toFormat("hh:mma"),
				end: DateTime.fromFormat(suggested.shiftEndTime, "HH:mm").toFormat("hh:mma"),
			},
			to: {
				start: DateTime.fromFormat(current.shiftStartTime, "HH:mm").toFormat("hh:mma"),
				end: DateTime.fromFormat(current.shiftEndTime, "HH:mm").toFormat("hh:mma"),
			},
		});
	}

	const matchesOnJob = jobManagementEnabled && Boolean(suggested.jobId);
	if (matchesOnJob) {
		if (suggested.jobId !== current.jobId) {
			changes.push({ field: "job", from: suggested.jobId, to: current.jobId });
		}
	} else if (!rolesStillCover(suggested.roles, current.roles)) {
		changes.push({ field: "roles", from: suggested.roles, to: current.roles });
	}

	return changes;
};

/**
 * Would saving cascade onto existing recurring shifts and their generated shifts? Mirrors the
 * server's gate (`shift_relevant_fields_changed`), so the "apply from" date can be disabled exactly
 * when it would have no effect.
 *
 * Only these three (four, counting both times) exist on the shift side and get written through.
 * Notably NOT `roles` — role lives on the nurse and only *selects* which templates match — nor any
 * of the staffing fields (`dayAmounts`, ratio ladder, rooms...), which have no counterpart at all.
 *
 * Deliberately diffs against the stored row rather than a snapshot of the form's initial state:
 * `getInitialConfigData` rewrites the times for medical departments and the job auto-populate effect
 * assigns one with no user action, so a snapshot would read "unchanged" while the payload really
 * does carry a new time or job. Comparing what we are about to send against what the server holds is
 * the same comparison the server makes, and it errs toward leaving the date picker enabled.
 */
export const willCascadeOntoShifts = (existing: DepartmentLocation, next: LocationConfigSlimData): boolean =>
	// Compared as clock times, as the server compares them: "07:00" (the form) is the stored "7:00" or "07:00:00".
	!sameClockTime(next.shiftStartTime, existing.shift_start) ||
	!sameClockTime(next.shiftEndTime, existing.shift_end) ||
	next.name.trim() !== existing.location ||
	next.jobId !== (existing.jobId ?? "");
